import { useState, useEffect } from 'react';
import { Card, Heading, Button, toaster, majorScale, Tablist} from 'evergreen-ui';
import { EditIcon, TrashIcon, PlusIcon, CircleArrowLeftIcon, FolderNewIcon} from 'evergreen-ui';
import SectionList from './SectionList';
import AddSectionDialog from './AddSectionDialog';
import EditSectionDialog from './EditSectionDialog';
import RemoveSectionFormDialog from './RemoveSectionFormDialog';
import sectionServices from '../services/sectionServices';
import attachmentServices from '../services/attachmentServices';
import AttachmentList from './AttachmentList';
import AddAttachmentForm from './AddAttachmentForm';
import SubsectionsList from './SubsectionsList';

const Archive = ({ isDirector, attachments, setAttachments }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newAttachment, setNewAttachment] = useState({ file: null, section: null });
    const [isLoading, setIsLoading] = useState(false);
    const [sections, setSections] = useState([]);
    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedSubsection, setSelectedSubsection] = useState(null);
    const [isAddDialogShown, setIsAddDialogShown] = useState(false);
    const [isEditDialogShown, setIsEditDialogShown] = useState(false);
    const [isRemoveDialogShown, setIsRemoveDialogShown] = useState(false);

    useEffect(() => {
        const loadSections = async () => {
            try {
                const sectionsData = await sectionServices.fetchSections();
                setSections(sectionsData.map(section => ({
                    ...section,
                    is_visible_to_educators: section.is_visible_to_educators || false
                })));
            } catch (error) {
                console.error('Errore nel caricamento delle sezioni:', error);
                toaster.danger('Errore nel caricamento delle sezioni.');
            }
        };

        loadSections();
    }, []);

    useEffect(() => {
        if (!selectedSection) {
            setSelectedSubsection(null);
            setAttachments([]);
            return;
        }

        const loadAttachments = async () => {
            try {
                setIsLoading(true);
                const raw = selectedSubsection
                    ? await attachmentServices.fetchAttachments(null, selectedSubsection)
                    : await attachmentServices.fetchAttachments(selectedSection, null);
                let attachmentsData = Array.isArray(raw) ? raw : [];
                if (!selectedSubsection) {
                    attachmentsData = attachmentsData.filter(a => String(a.source || 'section') !== 'subsection');
                }
                setAttachments(attachmentsData);
            } catch (error) {
                console.error('Errore nel caricamento degli allegati:', error);
                toaster.danger('Errore nel caricamento degli allegati.');
            } finally {
                setIsLoading(false);
            }
        };

        loadAttachments();
    }, [selectedSection, selectedSubsection, isAdding, setAttachments]);

    const handleAttachmentAdded = (newAttachment) => {
        setAttachments((prev) => [...prev, newAttachment]);
        setIsAdding(false);
    };

    const handleSectionAdded = (newSection) => {
        setSections((prev) => [...prev, newSection]);
        setIsAddDialogShown(false);
    };

    const handleSectionUpdated = (updatedSection) => {
        setSections((prev) => {
            const updatedSections = prev.map((section) =>
                section.id === updatedSection.id ? { ...updatedSection } : section
            );
            return updatedSections;
        });
        setIsEditDialogShown(false);
    };

    const handleSectionRemoved = (removedId) => {
        setSections((prev) => prev.filter((section) => section.id !== removedId));
        setIsRemoveDialogShown(false);
    };

    if (isAdding) {
        return (
            <Card elevation={2} padding={24} background="tint1">
                <Heading size={700} marginBottom={16}>
                    Aggiungi nuovo documento
                </Heading>
                <AddAttachmentForm
                    newAttachment={newAttachment}
                    setNewAttachment={setNewAttachment}
                    setIsAdding={setIsAdding}
                    setAttachments={handleAttachmentAdded}
                />
                <Button appearance="primary" intent="success" marginTop={majorScale(2)} onClick={() => setIsAdding(false)}>
                    Torna indietro <CircleArrowLeftIcon marginLeft={8} />
                </Button>
            </Card>
        );
    }

    return (
        <Card elevation={2} padding={24} background="tint1">
            <Heading size={700} marginBottom={16}>Archivio</Heading>
            <Tablist marginBottom={16} display="flex" flexWrap="wrap" alignItems="center">
                <SectionList
                    sections={sections}
                    selectedSection={selectedSection}
                    onSelectSection={setSelectedSection}
                    userRole={isDirector ? 'direttore' : 'educatore'}
                />
                {isDirector && (
                    <>
                        <Button onClick={() => setIsAddDialogShown(true)} marginRight={8}><FolderNewIcon /></Button>
                        <Button onClick={() => setIsEditDialogShown(true)} marginRight={8}><EditIcon /></Button>
                        <Button onClick={() => setIsRemoveDialogShown(true)} intent="danger"><TrashIcon /></Button>
                    </>
                )}
            </Tablist>
            {selectedSection && (
                <SubsectionsList sectionId={selectedSection} isDirector={isDirector} userRole={isDirector ? 'direttore' : 'educatore'} selectedSubsectionId={selectedSubsection} onSelectSubsection={setSelectedSubsection} />
            )}
            {isLoading ? (
                <Heading size={400} color="muted">
                    Caricamento documenti in corso...
                </Heading>
            ) : selectedSection ? (
                attachments.length > 0 ? (
                    <AttachmentList 
                        attachments={attachments} 
                        setAttachments={setAttachments} 
                        isDirector={isDirector} 
                    />
                ) : (
                    <Heading size={400} color="muted">
                        Nessun documento disponibile in questa cartella.
                    </Heading>
                )
            ) : (
                <Heading size={400} color="muted">
                    Nessuna cartella selezionata.
                </Heading>
            )}
            {isDirector && (
                <Button
                    appearance="primary"
                    intent="success"
                    marginTop={16}
                    onClick={() => setIsAdding(true)}
                >
                    Aggiungi nuovo documento<PlusIcon marginLeft={8} />
                </Button>
            )}
            <AddSectionDialog
                isShown={isAddDialogShown}
                onSectionAdded={handleSectionAdded}
                onClose={() => setIsAddDialogShown(false)}
            />
            <EditSectionDialog
                isShown={isEditDialogShown}
                sections={sections}
                onSectionUpdated={handleSectionUpdated}
                onClose={() => setIsEditDialogShown(false)}
            />
            <RemoveSectionFormDialog
                isShown={isRemoveDialogShown}
                sections={sections}
                onSectionRemoved={handleSectionRemoved}
                onClose={() => setIsRemoveDialogShown(false)}
            />
        </Card>
    );
};

export default Archive;